import React, { useCallback, useEffect, useState } from 'react'
import styled from 'styled-components'
import { useNavigate, useParams } from 'react-router-dom'
import { ArrowLeft, IndianRupee, Receipt, RefreshCw, Sparkles } from 'lucide-react'
import { useAppSelector } from '../../../Redux/Hooks'
import {
  getApiErrorMessage,
  getImagePurchaseTransactions,
  getImageSaleEarnings,
} from '../../../Redux/ApiCalls/Dashboard/ImagePurchaseAPI'
import {
  ImagePurchaseTransaction,
  ImageSaleEarnings,
} from '../../../Data/imagePurchase.dto'
import { formatINR } from '../../atoms/Dashboard/Album/PricingBreakdownCard'
import LoadingDots from '../../atoms/Utlis/LoadinDots'
import MarketplaceEmptyState from './Marketplace/MarketplaceEmptyState'
import {
  PageContainer,
  ScrollBody,
  FormCard,
  CardHeader,
  CardIcon,
  CardTitle,
  CardSubtitle,
} from '../../molecules/Dashboard/SingleAlbumPage/albumFormSharedStyles'
import {
  Hero,
  HeroGlow,
  HeroRow,
  HeroLeft,
  BackButton,
  HeroText,
  HeroBadge,
  PageTitle,
  PageSubtitle,
} from '../../molecules/Dashboard/Member/memberFormStyles'

const formatDate = (iso: string | null) =>
  iso
    ? new Date(iso).toLocaleString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : '—'

const ImageSalesPage: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const projectId = Number(id)
  const navigate = useNavigate()
  const { currentAlbum } = useAppSelector((state) => state.album)

  const [earnings, setEarnings] = useState<ImageSaleEarnings | null>(null)
  const [transactions, setTransactions] = useState<ImagePurchaseTransaction[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  const load = useCallback(async () => {
    if (!projectId) return
    setLoading(true)
    setError('')
    try {
      const [earningsData, txns] = await Promise.all([
        getImageSaleEarnings({ project_id: projectId }),
        getImagePurchaseTransactions({ project_id: projectId, enrich: 1 }),
      ])
      setEarnings(earningsData)
      setTransactions(txns)
    } catch (err) {
      setError(getApiErrorMessage(err, 'Could not load image sales.'))
    } finally {
      setLoading(false)
    }
  }, [projectId])

  useEffect(() => {
    load()
  }, [load])

  const stats = earnings
    ? [
        { label: 'Gross sales', value: formatINR(earnings.gross_sales) },
        { label: 'Commission deducted', value: formatINR(earnings.commission_deducted), negative: true },
        { label: 'Fee paid by customers', value: formatINR(earnings.service_fee_paid_by_customers) },
        { label: 'Your payout', value: formatINR(earnings.studio_payout), highlight: true },
        { label: 'Paid orders', value: earnings.paid_orders.toLocaleString('en-IN') },
        { label: 'Images sold', value: earnings.images_sold.toLocaleString('en-IN') },
      ]
    : []

  return (
    <PageContainer>
      <Hero>
        <HeroGlow />
        <HeroRow>
          <HeroLeft>
            <BackButton type="button" onClick={() => navigate(-1)} aria-label="Go back">
              <ArrowLeft size={18} />
            </BackButton>
            <HeroText>
              <div className="flex flex-col">
                <HeroBadge>
                  <Sparkles size={12} />
                  Image sales
                </HeroBadge>
                <PageTitle className="linear-text">
                  {currentAlbum?.id === projectId ? currentAlbum.name : 'Album sales'}
                </PageTitle>
                <PageSubtitle>
                  Earnings, platform commission and gateway fees from paid image orders.
                </PageSubtitle>
              </div>
            </HeroText>
          </HeroLeft>
          <RefreshBtn type="button" onClick={load} disabled={loading}>
            <RefreshCw size={14} />
            Refresh
          </RefreshBtn>
        </HeroRow>
      </Hero>

      <ScrollBody>
        {loading ? (
          <Center>
            <LoadingDots />
          </Center>
        ) : error ? (
          <FormCard>
            <ErrorText role="alert">{error}</ErrorText>
          </FormCard>
        ) : (
          <>
            <FormCard>
              <CardHeader>
                <CardIcon $tone="#31a66a">
                  <IndianRupee size={18} />
                </CardIcon>
                <div>
                  <CardTitle>Earnings summary</CardTitle>
                  <CardSubtitle>Only paid orders are counted.</CardSubtitle>
                </div>
              </CardHeader>
              <StatGrid>
                {stats.map((s) => (
                  <Stat key={s.label} $highlight={s.highlight}>
                    <StatLabel>{s.label}</StatLabel>
                    <StatValue $negative={s.negative} $highlight={s.highlight}>
                      {s.value}
                    </StatValue>
                  </Stat>
                ))}
              </StatGrid>
            </FormCard>

            <FormCard>
              <CardHeader>
                <CardIcon $tone="#c94daa">
                  <Receipt size={18} />
                </CardIcon>
                <div>
                  <CardTitle>Orders</CardTitle>
                  <CardSubtitle>Commission and payout on each order.</CardSubtitle>
                </div>
              </CardHeader>

              {transactions.length === 0 ? (
                <MarketplaceEmptyState
                  icon={<Receipt size={28} />}
                  title="No image orders yet"
                  description="When customers buy photos from this album, orders will appear here."
                  padding="8px 0 16px"
                />
              ) : (
                <TableWrap>
                  <Table>
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Images</th>
                        <th>Image total</th>
                        <th>Commission</th>
                        <th>Gateway fee</th>
                        <th>Customer paid</th>
                        <th>You received</th>
                        <th>Status</th>
                        <th>Settled</th>
                      </tr>
                    </thead>
                    <tbody>
                      {transactions.map((t) => (
                        <tr key={t.purchase_id}>
                          <td>{formatDate(t.created_at)}</td>
                          <td>{t.media_ids?.length ?? 0}</td>
                          <td>{formatINR(t.amount)}</td>
                          <td>
                            {t.commission_enabled
                              ? `${formatINR(t.commission_amount)} (${t.commission_percent}%)`
                              : '—'}
                          </td>
                          <td>
                            {t.fee_payer === 'platform'
                              ? 'Platform'
                              : formatINR(t.convenience_fee)}
                          </td>
                          <td>{t.total_amount != null ? formatINR(t.total_amount) : '—'}</td>
                          <td className="payout">{formatINR(t.vendor_amount)}</td>
                          <td>
                            <StatusPill $paid={t.payment_status === 'PAID'}>
                              {t.gateway_order_status || t.payment_status}
                            </StatusPill>
                          </td>
                          <td>{formatDate(t.settled_at)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </TableWrap>
              )}
            </FormCard>
          </>
        )}
      </ScrollBody>
    </PageContainer>
  )
}

export default ImageSalesPage

const Center = styled.div`
  display: flex;
  justify-content: center;
  padding: 60px 0;
`

const ErrorText = styled.p`
  margin: 0;
  font-family: Montserrat, sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: #e44862;
`

const RefreshBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 40px;
  padding: 0 16px;
  border-radius: 12px;
  border: 1px solid rgba(201, 77, 170, 0.2);
  background: rgba(255, 255, 255, 0.85);
  font-family: Urbanist, sans-serif;
  font-size: 13px;
  font-weight: 700;
  color: #c94daa;
  cursor: pointer;
  flex-shrink: 0;
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`

const StatGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(170px, 1fr));
  gap: 12px;
`

const Stat = styled.div<{ $highlight?: boolean }>`
  padding: 14px;
  border-radius: 14px;
  border: 1px solid
    ${({ $highlight }) => ($highlight ? 'rgba(49, 166, 106, 0.25)' : 'rgba(23, 23, 23, 0.06)')};
  background: ${({ $highlight }) => ($highlight ? 'rgba(49, 166, 106, 0.06)' : '#fafafa')};
`

const StatLabel = styled.p`
  margin: 0 0 6px;
  font-family: Montserrat, sans-serif;
  font-size: 11px;
  font-weight: 600;
  color: rgba(23, 23, 23, 0.5);
`

const StatValue = styled.p<{ $negative?: boolean; $highlight?: boolean }>`
  margin: 0;
  font-family: Urbanist, sans-serif;
  font-size: 20px;
  font-weight: 800;
  color: ${({ $negative, $highlight }) =>
    $negative ? '#e44862' : $highlight ? '#31a66a' : '#171717'};
`

const TableWrap = styled.div`
  overflow-x: auto;
`

const Table = styled.table`
  width: 100%;
  min-width: 860px;
  border-collapse: collapse;
  font-family: Montserrat, sans-serif;
  font-size: 12px;

  th,
  td {
    padding: 10px 8px;
    text-align: left;
    border-bottom: 1px solid rgba(23, 23, 23, 0.06);
    white-space: nowrap;
  }

  th {
    font-size: 10px;
    font-weight: 700;
    color: rgba(23, 23, 23, 0.45);
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }

  td {
    font-weight: 600;
    color: #171717;
  }

  td.payout {
    color: #31a66a;
    font-weight: 700;
  }
`

const StatusPill = styled.span<{ $paid?: boolean }>`
  display: inline-block;
  padding: 3px 10px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
  background: ${({ $paid }) => ($paid ? 'rgba(49, 166, 106, 0.12)' : 'rgba(23, 23, 23, 0.06)')};
  color: ${({ $paid }) => ($paid ? '#31a66a' : 'rgba(23, 23, 23, 0.6)')};
`
